import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../hooks/useInView'
import Footer from '../landing/components/Footer'
import DiscoveryMockup from './components/DiscoveryMockup'
import ControlMockup from './components/ControlMockup'
import { RouteTransitionContext } from '../components/AnimatedRoutes'
import ctaIcon from '../assets/cta-02.svg'

/* ── Animation helpers ────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

function Section({
  children,
  className = '',
  immediate = false,
}: {
  children: React.ReactNode
  className?: string
  immediate?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { threshold: 0.15 })
  const isTransitioning = useContext(RouteTransitionContext)

  const shouldShow = immediate ? !isTransitioning : !isTransitioning && isInView

  return (
    <motion.div
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={shouldShow ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/* ── Page ─────────────────────────────────────────── */
export default function AgentsPage() {
  return (
    <div className="min-h-screen bg-oak-cream">
      {/* HERO */}
      <section className="rounded-section bg-oak-cream pb-16 pt-28 md:pb-24 md:pt-36">
        <Section className="mx-auto max-w-nav px-6" immediate>
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold uppercase tracking-widest text-gold-500"
          >
            Agents
          </motion.span>
          <motion.h1
            variants={fadeUp}
            className="mt-4 font-serif text-[clamp(48px,8vw,120px)] leading-[0.85] tracking-[-2.88px] text-oak-black"
          >
            Sell beyond your community
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-[700px] text-[clamp(16px,2vw,22px)] font-medium leading-[1.3] tracking-[-0.2px] text-oak-black/70 md:mt-8"
          >
            Let AI agents like ChatGPT surface your products to buyers
            beyond your community — no extra lift, no loss of control.
          </motion.p>
        </Section>
      </section>

      {/* BUILT FOR DISCOVERY AND REACH */}
      <section className="rounded-section bg-oak-black px-6 py-20 md:py-32">
        <Section className="mx-auto max-w-nav">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:items-center md:gap-16 lg:gap-24">
            <div>
              <motion.h2
                variants={fadeUp}
                className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-white"
              >
                Built for discovery
              </motion.h2>
              <motion.div variants={fadeUp} className="mt-8 space-y-5">
                <p className="text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-white/70">
                  Oak plugs directly into the agentic commerce ecosystem.
                  Your products become discoverable through
                  AI agents — bringing new demand to your community without
                  changing how you work.
                </p>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} className="relative md:pb-12">
              <DiscoveryMockup />
            </motion.div>
          </div>
        </Section>
      </section>

      {/* GO BEYOND YOUR NETWORK */}
      <section className="bg-oak-cream px-6 py-20 md:py-32">
        <Section className="mx-auto max-w-nav">
          <motion.h2
            variants={fadeUp}
            className="text-center font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-oak-black"
          >
            Break the network ceiling
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mx-auto mt-6 max-w-[560px] text-center text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-oak-black/70"
          >
            Most licensors only grow as fast as their community can reach. Agentic
            commerce changes that.
          </motion.p>

          <div className="mt-12 grid grid-cols-1 gap-5 md:mt-16 md:grid-cols-3 md:gap-6">
            {[
              {
                title: 'Reach beyond your network',
                copy: "Your listings become discoverable to buyers who'd never find your community on their own.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                    <circle cx="12" cy="12" r="2.5" fill="currentColor" />
                    <circle
                      cx="12"
                      cy="12"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      opacity="0.5"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      opacity="0.25"
                    />
                  </svg>
                ),
              },
              {
                title: 'Grow without extra spend',
                copy: 'AI-driven discovery scales sales without scaling ad budget or acquisition effort.',
                icon: (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-6 w-6"
                  >
                    <path d="M3 18l6-6 3.5 3.5L21 7" />
                    <path d="M15 7h6v6" />
                  </svg>
                ),
              },
              {
                title: 'Demand finds you',
                copy: 'Agents surface the right products to the right buyers — no extra work on your end.',
                icon: (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-6 w-6"
                  >
                    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
                    <path d="M3 3l4 4M21 3l-4 4M3 21l4-4M21 21l-4-4" />
                  </svg>
                ),
              },
            ].map((card) => (
              <motion.div
                key={card.title}
                variants={fadeUp}
                className="rounded-2xl border border-oak-black/[0.06] bg-white p-6 md:p-8"
                style={{
                  boxShadow:
                    '0 2px 8px rgba(0,0,0,0.03), 0 0 1px rgba(0,0,0,0.04)',
                }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-oak-green/10 text-oak-green">
                  {card.icon}
                </div>
                <h3 className="mt-5 text-[clamp(18px,2vw,22px)] font-semibold tracking-[-0.3px] text-oak-black">
                  {card.title}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
                  {card.copy}
                </p>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* YOU STAY IN CONTROL */}
      <section className="rounded-section bg-white px-6 py-20 md:py-32">
        <Section className="mx-auto max-w-nav">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:items-start md:gap-16 lg:gap-24">
            <div>
              <motion.h2
                variants={fadeUp}
                className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-oak-black"
              >
                You stay in control
              </motion.h2>
              <motion.div variants={fadeUp} className="mt-8">
                <ul className="space-y-3">
                  {[
                    'Enable or disable discovery at any time',
                    'Control which platforms and agents get access',
                    'Maintain full ownership of your IP and data',
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-oak-black"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="mt-1 shrink-0 text-oak-green"
                      >
                        <path
                          d="M16.667 5L7.5 14.167 3.333 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} className="relative md:pb-20">
              <ControlMockup />
            </motion.div>
          </div>
        </Section>
      </section>

      {/* FINAL CTA */}
      <section className="rounded-section bg-oak-butter py-24 md:py-40">
        <Section className="mx-auto flex max-w-nav flex-col items-center px-6 text-center">
          <motion.h2
            variants={fadeUp}
            className="font-serif text-[14vw] leading-[0.85] tracking-[-2.88px] text-oak-black md:text-[clamp(48px,8vw,120px)]"
          >
            Discover new demand
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-[570px] text-[clamp(16px,2vw,26px)] font-medium leading-[1.19] tracking-[-0.26px] text-oak-black/60 md:mt-12"
          >
            Keep your community at the center while expanding your reach beyond it.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="mt-10 md:mt-16"
          >
            <a
              href="#demo"
              className="inline-flex items-center gap-3 rounded-pill bg-oak-black px-6 py-3.5 text-[clamp(16px,1.5vw,21px)] font-medium tracking-[-0.63px] text-white transition-all hover:opacity-80"
            >
              Request a Demo
              <img src={ctaIcon} alt="" className="h-8 w-8" />
            </a>
          </motion.div>
        </Section>
      </section>

      <Footer />
    </div>
  )
}
